import React from 'react';
import LgmButton from '../LgmButton';
import { SourcePanelPalette } from 'theme/sourcePanelColors';

interface SourcePanelEmptyStateProps {
  /** Small uppercase badge text — every source panel uses "FDA" today. */
  badgeText: string;
  /** Human-readable source name, e.g. "Orange Book", "NDC", "USDMF". */
  sourceLabel: string;
  palette: SourcePanelPalette;
  /** Admin AND edit mode, per Requirement 3 — the add control is a write action. */
  canAdd: boolean;
  onAdd: () => void;
}

/**
 * Zero-links render for the three source panels (issue #622, Sub-task 8.1).
 * Replaces the previous self-hiding `return null` — every role sees the
 * panel's chrome; only the body differs:
 *   - admin, edit mode: a compact "add-only" view with a control that will
 *     open the Task 9 Link-to-Source modal (not built yet — see `onAdd`)
 *   - everyone else: a plain "No link to [Source]" message
 */
const SourcePanelEmptyState: React.FC<SourcePanelEmptyStateProps> = ({
  badgeText,
  sourceLabel,
  palette,
  canAdd,
  onAdd,
}) => (
  <div
    style={{
      margin: '20px 0 8px 0',
      border: `1px solid ${palette.border}`,
      borderRadius: '6px',
      overflow: 'hidden',
      backgroundColor: palette.background,
    }}>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        padding: '8px 12px',
        backgroundColor: palette.headerBackground,
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span
          style={{
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: palette.badgeText,
            backgroundColor: palette.badgeBackground,
            padding: '2px 6px',
            borderRadius: '4px',
          }}>
          {badgeText} - {sourceLabel}
        </span>
      </div>
      {canAdd && (
        <LgmButton
          variant="outlined"
          onClick={onAdd}
          style={{ fontSize: '12px', padding: '4px 10px' }}>
          + Link to {sourceLabel}
        </LgmButton>
      )}
    </div>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '8px 12px',
        fontSize: '13px',
        color: palette.headerText,
      }}>
      <span>No link to {sourceLabel}.</span>
    </div>
  </div>
);

export default SourcePanelEmptyState;
